import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import API_URL from '../api';

export default function JobReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [job, setJob] = useState(null);
  const [settings, setSettings] = useState({ company_name: 'JobCostNow', logo_url: '', phone: '', address: '', brand_color: '#2E4057' });

  useEffect(() => {
    axios.get(`${API_URL}/api/jobs/${id}`, { headers }).then(res => setJob(res.data)).catch(() => {});
    axios.get(`${API_URL}/api/settings`).then(res => {
      if (res.data && res.data.company_name) {
        setSettings({
          company_name: res.data.company_name || 'JobCostNow',
          logo_url: res.data.logo_url || '',
          phone: res.data.phone || '',
          address: res.data.address || '',
          brand_color: res.data.brand_color || '#2E4057'
        });
      }
    }).catch(() => {});
  }, [id]);

  const fmt = (n) => '$' + parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
  const payTypeLabel = (t) => t === 'piece' ? 'Piece Rate' : t === 'commission' ? 'Commission' : 'Hourly';

  if (!job) return <div style={{textAlign:'center',padding:'60px'}}>Loading...</div>;

  const totalCOs = job.change_orders?.reduce((s, co) => s + parseFloat(co.amount), 0) || 0;
  const totalPaid = job.payments?.reduce((s, p) => s + parseFloat(p.amount), 0) || 0;
  const totalMaterials = job.materials?.reduce((s, m) => s + parseFloat(m.total_cost), 0) || 0;
  const totalLabor = job.labor?.reduce((s, l) => s + parseFloat(l.total), 0) || 0;
  const revisedContract = parseFloat(job.contract_amount) + totalCOs;
  const balanceDue = revisedContract - totalPaid;
  const totalCosts = totalMaterials + totalLabor;
  const grossProfit = revisedContract - totalCosts;
  const grossMargin = revisedContract > 0 ? (grossProfit / revisedContract * 100).toFixed(1) : 0;
  const brand = settings.brand_color || '#2E4057';

  return (
    <div style={styles.wrap}>
      <div style={styles.toolbar} className="no-print">
        <button onClick={() => navigate(`/jobs/${id}`)} style={styles.back}>&larr; Return to Job</button>
        <button onClick={() => window.print()} style={{...styles.printBtn, background: brand}}>Print / Save PDF</button>
      </div>

      <div style={styles.page} id="report">
        {/* Accent stripe */}
        <div style={{...styles.stripe, background: brand}}></div>

        {/* Header */}
        <div style={styles.repHeader}>
          <div style={styles.headLeft}>
            {settings.logo_url
              ? <img src={settings.logo_url} alt={settings.company_name} style={styles.logo} onError={e => { e.target.style.display='none'; }} />
              : <div style={{...styles.coName, color: brand}}>{settings.company_name}</div>}
            <div style={styles.coContact}>
              {settings.address ? <div>{settings.address}</div> : null}
              {settings.phone ? <div>{settings.phone}</div> : null}
            </div>
          </div>
          <div style={styles.headRight}>
            <div style={{...styles.repTitle, color: brand}}>JOB COST REPORT</div>
            <div style={styles.repMeta}>{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
            <div style={styles.confidential}>CONFIDENTIAL &middot; INTERNAL</div>
          </div>
        </div>

        {/* Job info band */}
        <div style={styles.infoBand}>
          <div style={styles.infoCell}><span style={styles.lbl}>Customer</span><span style={styles.infoVal}>{job.customer_name}</span></div>
          <div style={styles.infoCell}><span style={styles.lbl}>Address</span><span style={styles.infoVal}>{job.job_address || '\u2014'}</span></div>
          <div style={styles.infoCell}><span style={styles.lbl}>Job Number</span><span style={styles.infoVal}>{job.job_number || '\u2014'}</span></div>
          <div style={styles.infoCell}><span style={styles.lbl}>Status</span><span style={styles.infoVal}>{job.status || '\u2014'}</span></div>
        </div>

        {/* Headline metrics */}
        <div style={styles.metricRow}>
          <div style={styles.metric}><div style={styles.metricLbl}>REVISED CONTRACT</div><div style={{...styles.metricVal, color: brand}}>{fmt(revisedContract)}</div></div>
          <div style={styles.metric}><div style={styles.metricLbl}>GROSS PROFIT</div><div style={{...styles.metricVal, color: '#1E7145'}}>{fmt(grossProfit)}</div></div>
          <div style={styles.metric}><div style={styles.metricLbl}>GROSS MARGIN</div><div style={{...styles.metricVal, color: '#1E7145'}}>{grossMargin}%</div></div>
          <div style={styles.metricLast}><div style={styles.metricLbl}>BALANCE DUE</div><div style={{...styles.metricVal, color: '#C0392B'}}>{fmt(balanceDue)}</div></div>
        </div>

        {/* Financial Summary */}
        <div style={{...styles.secBar, borderLeft: `4px solid ${brand}`}}>FINANCIAL SUMMARY</div>
        <table style={styles.sumTable}>
          <tbody>
            <tr><td style={styles.sLabel}>Original Contract</td><td style={styles.sVal}>{fmt(job.contract_amount)}</td></tr>
            <tr><td style={styles.sLabel}>Change Orders</td><td style={styles.sVal}>{fmt(totalCOs)}</td></tr>
            <tr style={styles.hl}><td style={styles.sLabelB}>Revised Contract</td><td style={styles.sValB}>{fmt(revisedContract)}</td></tr>
            <tr><td style={styles.sLabel}>Materials Cost</td><td style={styles.sVal}>{fmt(totalMaterials)}</td></tr>
            <tr><td style={styles.sLabel}>Labor Cost</td><td style={styles.sVal}>{fmt(totalLabor)}</td></tr>
            <tr style={styles.hl}><td style={styles.sLabelB}>Total Costs</td><td style={styles.sValB}>{fmt(totalCosts)}</td></tr>
            <tr style={styles.profit}><td style={styles.sLabelB}>Gross Profit</td><td style={styles.sValB}>{fmt(grossProfit)}</td></tr>
            <tr style={styles.profit}><td style={styles.sLabelG}>Gross Margin</td><td style={styles.sValG}>{grossMargin}%</td></tr>
            <tr><td style={styles.sLabel}>Total Paid</td><td style={styles.sVal}>{fmt(totalPaid)}</td></tr>
            <tr style={styles.due}><td style={styles.sLabelR}>Balance Due</td><td style={styles.sValR}>{fmt(balanceDue)}</td></tr>
          </tbody>
        </table>

        {/* Change Orders */}
        <div style={{...styles.secBar, borderLeft: `4px solid ${brand}`}}>CHANGE ORDERS</div>
        {(!job.change_orders || job.change_orders.length === 0) ? <p style={styles.empty}>None recorded</p> : (
          <table style={styles.dtable}>
            <thead><tr style={styles.headRowBg}><th style={styles.th}>Date</th><th style={styles.th}>Description</th><th style={styles.thR}>Amount</th><th style={styles.thC}>Status</th></tr></thead>
            <tbody>{job.change_orders.map((co, i) => (
              <tr key={i} style={i%2 ? styles.zebra : null}>
                <td style={styles.td}>{co.co_date ? new Date(co.co_date).toLocaleDateString() : '\u2014'}</td>
                <td style={styles.td}>{co.description}</td>
                <td style={styles.tdR}>{fmt(co.amount)}</td>
                <td style={styles.tdC}><span style={{color: co.signed ? '#1E7145' : '#E67E22', fontWeight: 600, fontSize: '11px'}}>{co.signed ? 'SIGNED' : 'VERBAL'}</span></td>
              </tr>
            ))}</tbody>
            <tfoot><tr><td colSpan={2} style={styles.ftL}>Total Change Orders</td><td style={styles.ftR}>{fmt(totalCOs)}</td><td style={styles.ftBlank}></td></tr></tfoot>
          </table>
        )}

        {/* Materials */}
        <div style={{...styles.secBar, borderLeft: `4px solid ${brand}`}}>MATERIALS</div>
        {(!job.materials || job.materials.length === 0) ? <p style={styles.empty}>None recorded</p> : (
          <table style={styles.dtable}>
            <thead><tr style={styles.headRowBg}><th style={styles.th}>Date</th><th style={styles.th}>Description</th><th style={styles.th}>Vendor</th><th style={styles.thR}>Qty</th><th style={styles.thR}>Unit</th><th style={styles.thR}>Total</th></tr></thead>
            <tbody>{job.materials.map((m, i) => (
              <tr key={i} style={i%2 ? styles.zebra : null}>
                <td style={styles.td}>{m.material_date ? new Date(m.material_date).toLocaleDateString() : '\u2014'}</td>
                <td style={styles.td}>{m.description}</td>
                <td style={styles.td}>{m.vendor || '\u2014'}</td>
                <td style={styles.tdR}>{m.quantity}</td>
                <td style={styles.tdR}>{fmt(m.unit_cost)}</td>
                <td style={styles.tdR}>{fmt(m.total_cost)}</td>
              </tr>
            ))}</tbody>
            <tfoot><tr><td colSpan={5} style={styles.ftL}>Total Materials</td><td style={styles.ftR}>{fmt(totalMaterials)}</td></tr></tfoot>
          </table>
        )}

        {/* Labor */}
        <div style={{...styles.secBar, borderLeft: `4px solid ${brand}`}}>LABOR</div>
        {(!job.labor || job.labor.length === 0) ? <p style={styles.empty}>None recorded</p> : (
          <table style={styles.dtable}>
            <thead><tr style={styles.headRowBg}><th style={styles.th}>Date</th><th style={styles.th}>Employee</th><th style={styles.th}>Type</th><th style={styles.th}>Description</th><th style={styles.thR}>Hours</th><th style={styles.thR}>Rate</th><th style={styles.thR}>Total</th></tr></thead>
            <tbody>{job.labor.map((l, i) => (
              <tr key={i} style={i%2 ? styles.zebra : null}>
                <td style={styles.td}>{l.labor_date ? new Date(l.labor_date).toLocaleDateString() : '\u2014'}</td>
                <td style={styles.td}>{l.employee}</td>
                <td style={styles.td}>{payTypeLabel(l.pay_type)}</td>
                <td style={styles.td}>{l.description || '\u2014'}</td>
                <td style={styles.tdR}>{l.pay_type === 'hourly' ? l.hours : '\u2014'}</td>
                <td style={styles.tdR}>{l.pay_type === 'hourly' ? fmt(l.rate) : '\u2014'}</td>
                <td style={styles.tdR}>{fmt(l.total)}</td>
              </tr>
            ))}</tbody>
            <tfoot><tr><td colSpan={6} style={styles.ftL}>Total Labor</td><td style={styles.ftR}>{fmt(totalLabor)}</td></tr></tfoot>
          </table>
        )}

        <div style={styles.repFoot}>
          <span>{settings.company_name}{settings.phone ? '  \u00b7  ' + settings.phone : ''}</span>
          <span>Confidential &mdash; Internal Use Only</span>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          #report { box-shadow: none !important; margin: 0 !important; max-width: 100% !important; border-radius: 0 !important; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; }
          @page { margin: 0.5in; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  wrap: { minHeight: '100vh', background: '#eef1f5', fontFamily: "'Helvetica Neue', Arial, sans-serif", padding: '24px 0' },
  toolbar: { maxWidth: '820px', margin: '0 auto 18px', display: 'flex', justifyContent: 'space-between', padding: '0 16px' },
  back: { background: '#fff', color: '#2E4057', border: '1px solid #ccc', borderRadius: '6px', padding: '10px 18px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  printBtn: { background: '#2E4057', color: '#fff', border: 'none', borderRadius: '6px', padding: '10px 22px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  page: { maxWidth: '820px', margin: '0 auto', background: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.10)', borderRadius: '4px', overflow: 'hidden' },
  stripe: { height: '6px', width: '100%' },
  repHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '32px 40px 20px' },
  headLeft: {},
  logo: { maxHeight: '64px', maxWidth: '240px', objectFit: 'contain', marginBottom: '8px' },
  coName: { fontSize: '26px', fontWeight: 'bold', letterSpacing: '-0.5px' },
  coContact: { fontSize: '11.5px', color: '#888', lineHeight: 1.5 },
  headRight: { textAlign: 'right' },
  repTitle: { fontSize: '17px', fontWeight: 'bold', letterSpacing: '1.5px' },
  repMeta: { fontSize: '12px', color: '#777', marginTop: '4px' },
  confidential: { fontSize: '9px', color: '#bbb', letterSpacing: '1px', marginTop: '6px' },
  infoBand: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '0', background: '#f6f8fa', borderTop: '1px solid #eceff2', borderBottom: '1px solid #eceff2', padding: '14px 40px' },
  infoCell: { display: 'flex', flexDirection: 'column' },
  lbl: { fontSize: '9px', color: '#9aa5b1', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '3px' },
  infoVal: { fontSize: '13.5px', color: '#2E4057', fontWeight: 600 },
  metricRow: { display: 'flex', padding: '0 40px', margin: '22px 0 8px' },
  metric: { flex: 1, borderRight: '1px solid #eceff2', paddingRight: '16px' },
  metricLast: { flex: 1, paddingLeft: '16px' },
  metricLbl: { fontSize: '9px', color: '#9aa5b1', letterSpacing: '0.8px', marginBottom: '4px' },
  metricVal: { fontSize: '20px', fontWeight: 'bold', letterSpacing: '-0.5px' },
  secBar: { fontSize: '11px', fontWeight: 'bold', color: '#2E4057', letterSpacing: '1px', padding: '7px 0 7px 12px', margin: '26px 40px 10px', background: '#f6f8fa' },
  sumTable: { width: 'calc(100% - 80px)', margin: '0 40px', borderCollapse: 'collapse', fontSize: '13.5px' },
  sLabel: { padding: '7px 8px', color: '#555', borderBottom: '1px solid #f2f4f6' },
  sVal: { padding: '7px 8px', textAlign: 'right', color: '#333', borderBottom: '1px solid #f2f4f6', fontVariantNumeric: 'tabular-nums' },
  sLabelB: { padding: '8px', fontWeight: 'bold', color: '#2E4057' },
  sValB: { padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#2E4057', fontVariantNumeric: 'tabular-nums' },
  sLabelG: { padding: '7px 8px', color: '#1E7145', fontWeight: 600 },
  sValG: { padding: '7px 8px', textAlign: 'right', color: '#1E7145', fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  sLabelR: { padding: '8px', fontWeight: 'bold', color: '#C0392B' },
  sValR: { padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#C0392B', fontVariantNumeric: 'tabular-nums' },
  hl: { background: '#f0f4f8' },
  profit: { background: '#EAF6EE' },
  due: { background: '#FBEAE8' },
  dtable: { width: 'calc(100% - 80px)', margin: '0 40px', borderCollapse: 'collapse', fontSize: '12.5px' },
  headRowBg: { background: '#2E4057' },
  th: { padding: '8px', textAlign: 'left', fontSize: '9.5px', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 },
  thR: { padding: '8px', textAlign: 'right', fontSize: '9.5px', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 },
  thC: { padding: '8px', textAlign: 'center', fontSize: '9.5px', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 },
  zebra: { background: '#f8fafb' },
  td: { padding: '7px 8px', color: '#333', borderBottom: '1px solid #f2f4f6' },
  tdR: { padding: '7px 8px', textAlign: 'right', color: '#333', borderBottom: '1px solid #f2f4f6', fontVariantNumeric: 'tabular-nums' },
  tdC: { padding: '7px 8px', textAlign: 'center', borderBottom: '1px solid #f2f4f6' },
  ftL: { padding: '9px 8px', textAlign: 'right', fontWeight: 'bold', color: '#2E4057', borderTop: '2px solid #2E4057', fontSize: '12px' },
  ftR: { padding: '9px 8px', textAlign: 'right', fontWeight: 'bold', color: '#2E4057', borderTop: '2px solid #2E4057', fontVariantNumeric: 'tabular-nums', fontSize: '12px' },
  ftBlank: { borderTop: '2px solid #2E4057' },
  empty: { color: '#aaa', fontSize: '12.5px', fontStyle: 'italic', margin: '4px 40px 12px' },
  repFoot: { marginTop: '36px', padding: '14px 40px', borderTop: '1px solid #eceff2', fontSize: '10px', color: '#aaa', display: 'flex', justifyContent: 'space-between' },
};
